/* eslint-disable @typescript-eslint/no-explicit-any */

import type { ResourceWithSlug } from "./ResourceWithSlug";
import type { ActionTemplateParameterDisplaySettings, ActionTemplateParameterResource } from "./actionTemplateParameterResource";
import type { DeploymentActionPackageResource } from "./deploymentActionPackageResource";
import type { MetadataTypeCollection } from "./dynamicFormResources";
import type { EnvironmentResource } from "./environmentResource";
import type { ExtensionSettingsValues } from "./extensionSettingsValues";
import type { IconResource } from "./iconResource";
import type { NamedResource } from "./namedResource";
import type { PlatformHubVersionControlSettingsResource } from "./platformHubVersionControlSettingsResource";
import type { PropertyValueResource, SensitiveValue } from "./propertyValueResource";
import type { IIdName, ResourceWithLinks } from "./resource";
import type { NewSpaceScopedResource, SpaceScopedResource } from "./spaceScopedResource";
import type { TenantedDeploymentMode } from "./tenantedDeploymentMode";
import type { GitRef } from "./versionControlledResource";
import { isGitBranch, isGitCommit, isGitTag } from "./versionControlledResource";
import type { WorkerPoolResource } from "./workerpoolResource";

interface BaseProjectResourceLinks {
    Self: string;
    Releases: string;
    Channels: string;
    Triggers: string;
    ScheduledTriggers: string;
    OrderChannels: string;
    Variables: string;
    Progression: string;
    RunbookTaskRunDashboardItemsTemplate: string;
    DeploymentProcess: string;
    Web: string;
    Logo: string;
    Metadata: string;
    Runbooks: string;
    RunbookSnapshots: string;
    Summary: string;
    DeploymentSettings: string;
    InsightsMetrics: string;
}

type VcsProjectResourceLinks = BaseProjectResourceLinks & {
    Branches: string;
    Commits: string;
    Tags: string;
    MigrateVariablesToGit: string;
};

type DatabaseProjectResourceLinks = BaseProjectResourceLinks & {
    ConvertToGit: string;
};

type ProjectResourceLinks = VcsProjectResourceLinks | DatabaseProjectResourceLinks;

export enum PersistenceSettingsType {
    VersionControlled = "VersionControlled",
    Database = "Database",
}

export type PowerShellEdition = "Desktop" | "Core";

export type PersistenceSettings = GitPersistenceSettings | DatabasePersistenceSettings | PlatformHubVersionControlSettingsResource;

export interface DatabasePersistenceSettings {
    Type: PersistenceSettingsType.Database;
}

export interface GitPersistenceSettings {
    Type: PersistenceSettingsType.VersionControlled;
    Credentials: AnonymousVcsCredentials | UsernamePasswordVcsCredentials | ReferenceVcsCredentials | GitHubVcsCredentials;
    ConversionState: ConversionState;
    Url: string;
    DefaultBranch: string;
    BasePath: string;
    ProtectedDefaultBranch: boolean;
    ProtectedBranchNamePatterns: string[];
}

export enum AuthenticationType {
    Anonymous = "Anonymous",
    UsernamePassword = "UsernamePassword",
    Reference = "Reference",
    GitHub = "GitHub",
}

export interface GitHubVcsCredentials {
    Type: AuthenticationType.GitHub;
    Id: string;
}

export interface ReferenceVcsCredentials {
    Type: AuthenticationType.Reference;
    Id: string;
}

export interface UsernamePasswordVcsCredentials {
    Type: AuthenticationType.UsernamePassword;
    Username: string;
    Password: SensitiveValue;
}

export const defaultUsernamePasswordVcsCredentials: UsernamePasswordVcsCredentials = {
    Type: AuthenticationType.UsernamePassword,
    Username: "",
    Password: {
        HasValue: false,
    },
};

export interface AnonymousVcsCredentials {
    Type: AuthenticationType.Anonymous;
}

export interface ConversionState {
    VariablesAreInGit: boolean;
    RunbooksAreInGit: boolean;
}

export function IsUsingUsernamePasswordAuth(T: AnonymousVcsCredentials | UsernamePasswordVcsCredentials | ReferenceVcsCredentials | GitHubVcsCredentials): T is UsernamePasswordVcsCredentials {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (T as UsernamePasswordVcsCredentials).Type === AuthenticationType.UsernamePassword;
}

export function IsUsingAnonymousAuth(T: AnonymousVcsCredentials | UsernamePasswordVcsCredentials | ReferenceVcsCredentials | GitHubVcsCredentials): T is AnonymousVcsCredentials {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (T as AnonymousVcsCredentials).Type === AuthenticationType.Anonymous;
}

export function IsUsingReferenceAuth(T: AnonymousVcsCredentials | UsernamePasswordVcsCredentials | ReferenceVcsCredentials | GitHubVcsCredentials): T is ReferenceVcsCredentials {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (T as ReferenceVcsCredentials).Type === AuthenticationType.Reference;
}

export function IsUsingGitHubAuth(T: AnonymousVcsCredentials | UsernamePasswordVcsCredentials | ReferenceVcsCredentials | GitHubVcsCredentials): T is GitHubVcsCredentials {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (T as GitHubVcsCredentials).Type === AuthenticationType.GitHub;
}

export function HasVariablesInGit(T: PersistenceSettings): boolean {
    return HasGitPersistenceSettings(T) && T.ConversionState?.VariablesAreInGit;
}

export function HasRunbooksInGit(T: PersistenceSettings): boolean {
    return HasGitPersistenceSettings(T) && T.ConversionState?.RunbooksAreInGit;
}

export function HasVcsProjectResourceLinks(links: ProjectResourceLinks): links is VcsProjectResourceLinks {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (links as VcsProjectResourceLinks).Branches !== undefined;
}

export function HasGitPersistenceSettings(T: PersistenceSettings): T is GitPersistenceSettings {
    return "Type" in T ? T.Type === PersistenceSettingsType.VersionControlled : false;
}

export interface ProjectTemplateDetailsResource {
    Slug: string;
    VersionMask: string;
    Version: string;
    IsShared?: boolean;
}

export interface ProjectResource extends NamedResource<ProjectResourceLinks>, SpaceScopedResource, ResourceWithSlug {
    VariableSetId: string;
    DeploymentProcessId: string;
    DiscreteChannelRelease: boolean;
    IncludedLibraryVariableSetIds: string[];
    TenantedDeploymentMode: TenantedDeploymentMode;
    ReleaseCreationStrategy: ReleaseCreationStrategy;
    Templates: ActionTemplateParameterResource[];
    AutoDeployReleaseOverrides: any[];
    ForcePackageDownload: boolean;
    LifecycleId: string;
    AutoCreateRelease: boolean;
    ClonedFromProjectId: string;
    ExtensionSettings: ExtensionSettingsValues[];
    IsVersionControlled: boolean;
    PersistenceSettings: GitPersistenceSettings | DatabasePersistenceSettings;
    Slug: string;
    ProjectGroupId: string;
    Description: string;
    IsDisabled: boolean;
    Icon?: IconResource;
    AllowIgnoreChannelRules: boolean;
    ExecuteDeploymentsOnResilientPipeline: boolean | null;
    ProvisioningRunbookId?: string;
    DeprovisioningRunbookId?: string;
    CombineHealthAndSyncStatusInDashboardLiveStatus: boolean;
    DefaultPowerShellEdition: PowerShellEdition | null;
    ProjectTemplateDetails: ProjectTemplateDetailsResource | null;
    ProjectTags: string[];
}

export interface GetAllProjectSummariesResponse {
    Projects: ProjectSummaryResource[];
}

export interface GetAllProjectSummariesAcrossAllSpacesResponse {
    Projects: ProjectSummaryResource[];
}

export interface ProjectSummaryResource extends IIdName, SpaceScopedResource {
    Slug: string;
    ProjectGroupId: string;
    Description: string;
    IsDisabled: boolean;
    Logo: string;
    TenantedDeploymentMode: TenantedDeploymentMode;
}

export interface NewProjectResource extends NewSpaceScopedResource {
    Name: string;
    Description: string;
    ProjectGroupId: string;
    LifecycleId: string;
    ProjectConnectivityPolicy: { AllowDeploymentsToNoTargets: boolean };
    ProjectTemplateDetails?: ProjectTemplateDetailsResource;
}

export interface ProjectSettingsMetadata {
    ExtensionId: string;
    Metadata: MetadataTypeCollection;
}

export interface ReleaseCreationStrategy {
    ReleaseCreationPackage: DeploymentActionPackageResource;
    ChannelId?: string;
    ReleaseCreationPackageStepId?: string;
}

export interface VersionControlCompatibilityResponse {
    Errors: string[];
    Warnings: string[];
    Notices: string[];
}

export enum ValidateGitRefV2ResponseType {
    Success = "Success",
    ProjectNotVersionControlled = "ProjectNotVersionControlled",
    ConnectionFailed = "ConnectionFailed",
    GitRefNotFound = "GitRefNotFound",
    OclNotFound = "OclNotFound",
}

export type ValidateGitRefV2Response = {
    Type: ValidateGitRefV2ResponseType;
    GitRef?: GitRefResource;
    Message?: string;
};

export type ConvertProjectToVersionControlledResponse = {};

export type MigrateRunbooksToGitResponse = {
    DraftRunbooks: RunbookSummary[];
    PublishedRunbooks: RunbookSummary[];
    ServerTaskId?: string;
};

export interface RunbookSummary {
    RunbookId: string;
    RunbookName: string;
}

export interface RecentlyViewedProjectIds {
    [key: string]: string[];
}

export interface ProjectSummary {
    HasRunbooks: boolean;
    HasDeploymentProcess: boolean;
    HasBeenSuccessfullyDeployed: boolean;
}

type GitRefResourceLinks = {
    DeploymentProcess: string;
    DeploymentSettings: string;
    ReleaseTemplate: string;
    Channels: string;
    Variables: string;
};

export interface GitRefResource extends ResourceWithLinks<GitRefResourceLinks> {
    Name: string;
    CanonicalName: GitRef;
}

export type GitBranchResource = GitRefResource & { IsProtected: boolean };
export type GitTagResource = GitRefResource;
export type GitNamedRefResource = GitRefResource & { IsProtected: boolean };
export type GitCommitResource = GitRefResource;

export type GitLinkResult = {
    Link: string | undefined;
};

export function isGitBranchResource(gitRef: GitRefResource): gitRef is GitBranchResource {
    return isGitBranch(gitRef.CanonicalName);
}

export function getURISafeGitRef(gitRef: GitRef): string {
    return encodeURIComponent(gitRef);
}

export function getGitRefFromRouteParameter(routeParameter: string | undefined): string | undefined {
    if (routeParameter) {
        return decodeURIComponent(routeParameter);
    }
}

export interface ProjectStatusResponse {
    SpaceHasEnvironments: boolean;
    HasSteps: boolean;
    HasStepsThatRequireTargets: boolean;
    UnassociatedTargetRoles: string[];
    HasBeenSuccessfullyDeployed: boolean;
    HasRunbooks: boolean;
    SpaceHasSuccessfulDeployments: boolean;
    IsVersionControlled: boolean;
    CloudTargetDiscoveryEnabled: boolean;
    SpaceHasKubernetesTarget: boolean;
    SpaceHasArgoCDInstance: boolean;
}

export interface DeploymentSummary {
    Id: string;
    Status: string;
    CompletedTime: string;
}

export interface ProjectIntentsResource {
    SpaceId: string;
    ProjectId: string;
    ProjectGroupId?: string;
    IsVcsProject?: boolean;
    ApplicationType?: string;
    BuildServer?: string;
    DeploymentLocation?: string;
    PackageLocation?: string;
    RunbookOnly?: boolean;
}

export interface GetAvailableEnvironmentsForProjectBffResponse {
    Environments: EnvironmentResource[];
}

export interface GetAvailableEnvironmentsForProjectsBffResponse {
    Environments: EnvironmentResource[];
    ProjectEnvironments: { [projectId: string]: string[] };
}

export interface GetAvailableEnvironmentsForProjectsAcrossAllSpacesBffResponse {
    Environments: EnvironmentResource[];
    ProjectEnvironments: { [projectId: string]: string[] };
}

export interface CreateSampleProjectBffResponse {
    Project: ProjectResource;
}

export function isProtectedBranch(gitRef: GitRefResource | undefined) {
    if (!gitRef) return false;

    if (isGitBranchResource(gitRef)) {
        return gitRef.IsProtected;
    }

    return false;
}

export function canCommitTo(gitRef: GitRefResource | undefined) {
    if (!gitRef) return false;

    if (isProtectedBranch(gitRef)) return false;
    if (isGitTag(gitRef.CanonicalName)) return false;
    if (isGitCommit(gitRef.CanonicalName)) return false;

    return true;
}

export interface GetProjectTemplatesWithValuesBffResponse {
    Variables: ProjectTemplateTenantValue[];
    TotalValuesCount: number;
    FilteredValuesCount: number;
    AnyMissingValues: boolean;
    MissingValueProjectTemplateIds: string[];
    Accounts: { [accountId: string]: string };
    Certificates: { [certificateId: string]: string };
    WorkerPools: { [workerPoolId: string]: WorkerPoolResource };
}

export interface GetScopedProjectTemplateValuesForProjectBffResponse {
    Variables: { [templateId: string]: ScopedProjectTemplateWithValues };
    TotalValuesCount: number;
    FilteredValuesCount: number;
    AnyMissingValues: boolean;
    MissingValueProjectTemplateIds: string[];
    Accounts: { [accountId: string]: string };
    Certificates: { [certificateId: string]: string };
    WorkerPools: { [workerPoolId: string]: WorkerPoolResource };
}

export interface ScopedProjectTemplateWithValues {
    Template: ScopedProjectTemplateInfo;
    Values: ScopedProjectTemplateTenantValue[];
}

export interface ScopedProjectTemplateInfo {
    Id: string;
    Name: string;
    Label: string;
    HelpText: string;
    DisplaySettings: { [key: string]: string };
}

export type ValueStatus = "missing" | "default" | "overridden";

export interface ProjectTemplateTenantValue {
    TemplateId: string;
    TenantId: string;
    TenantName: string;
    TenantLogoLink: string;
    TenantIsDisabled: boolean;
    EnvironmentId: string;
    EnvironmentName: string;
    Value: PropertyValueResource | undefined;
    ValueStatus: ValueStatus;
}

export interface CommonTemplateTenantValue {
    TemplateId: string;
    TenantId: string;
    TenantName: string;
    TenantLogoLink: string;
    TenantIsDisabled: boolean;
    Value: PropertyValueResource | undefined;
    ValueStatus: ValueStatus;
    Id: string;
}

export interface ScopedCommonTemplateTenantValue extends CommonTemplateTenantValue {
    Scope: CommonVariableScope;
}

export interface ScopedProjectTemplateTenantValue {
    TemplateId: string;
    TenantId: string;
    TenantName: string;
    TenantLogoLink: string;
    TenantIsDisabled: boolean;
    Value: PropertyValueResource | undefined;
    ValueStatus: ValueStatus;
    Scope: CommonVariableScope;
    Id: string;
}

export interface CommonVariableScope {
    EnvironmentIds: string[];
}

export function isProjectTemplateTenantValue(value: ProjectTemplateTenantValue | CommonTemplateTenantValue): value is ProjectTemplateTenantValue {
    return "EnvironmentId" in value;
}

export interface CommonTemplateInfo {
    Id: string;
    Name: string;
    Label: string;
    HelpText: string;
    DisplaySettings: ActionTemplateParameterDisplaySettings;
}

export interface TemplateWithValues {
    Template: CommonTemplateInfo;
    Values: CommonTemplateTenantValue[];
}

export interface ScopedTemplateWithValues {
    Template: CommonTemplateInfo;
    Values: ScopedCommonTemplateTenantValue[];
}

export interface LibraryVariableSetInfo {
    Id: string;
    Name: string;
    Description: string;
}

export interface LibraryVariableSetGroup {
    LibraryVariableSet: LibraryVariableSetInfo;
    Templates: TemplateWithValues[];
}

export interface ScopedLibraryVariableSetGroup {
    LibraryVariableSet: LibraryVariableSetInfo;
    Templates: ScopedTemplateWithValues[];
}

export interface GetCommonTemplatesWithValuesBffResponse {
    TotalValuesCount: number;
    FilteredValuesCount: number;
    VariableSets: LibraryVariableSetGroup[];
    AnyMissingValues: boolean;
    MissingValueLibraryVariableSetIds: string[];
    Accounts: { [accountId: string]: string };
    Certificates: { [certificateId: string]: string };
    WorkerPools: { [workerPoolId: string]: WorkerPoolResource };
}
export interface GetScopedCommonTemplatesWithValuesBffResponse extends Omit<GetCommonTemplatesWithValuesBffResponse, "VariableSets"> {
    VariableSets: ScopedLibraryVariableSetGroup[];
}

export interface GetProjectsAffectedByChangeBffResponse {
    TotalCount: number;
    DisplayedProjects: IIdName[];
    HiddenProjectsCount: number;
}

export interface ChangedCommonTemplateSets {
    SetId: string;
    TenantId: string;
}
